import * as E from "fp-ts/Either"
import { Store } from "@hoppscotch/common/kernel/store"

const namespace = "personal-environment-import-links.v1"
type Links = Record<string, string | null>
const writes = new Map<string, Promise<void>>()
const pending = new Map<string, Set<Promise<unknown>>>()

async function readLinks(userID: string): Promise<Links> {
  const result = await Store.get<Links>(namespace, userID)
  if (E.isLeft(result))
    throw new Error("Could not load environment import links")
  const value = result.right
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {}
}

/** IDs only, scoped to the importing account; no environment values are cached. */
export function recordEnvironmentImportLink(
  userID: string,
  sourceID: string,
  targetID: string | null
) {
  const write = (writes.get(userID) ?? Promise.resolve())
    .catch(() => {})
    .then(async () => {
      const links = await readLinks(userID)
      const result = await Store.set(namespace, userID, {
        ...links,
        [sourceID]: targetID,
      })
      if (E.isLeft(result))
        throw new Error("Could not save environment import link")
    })
  writes.set(userID, write)
  return write
}

export function trackEnvironmentImport(
  userID: string,
  operation: Promise<unknown>
) {
  const operations = pending.get(userID) ?? new Set<Promise<unknown>>()
  pending.set(userID, operations)
  operations.add(operation)
  void operation.finally(() => operations.delete(operation)).catch(() => {})
}

export async function waitForEnvironmentImports(userID: string) {
  while (pending.get(userID)?.size) await Promise.all([...pending.get(userID)!])
  await writes.get(userID)
}

export async function resolveImportedEnvironmentIDs(
  userID: string,
  ids: string[],
  available: () => string[]
) {
  await waitForEnvironmentImports(userID)
  const links = await readLinks(userID)
  const live = new Set(available())
  return [
    ...new Set(
      ids.map((id) => {
        const target = Object.hasOwn(links, id) ? links[id] : id
        if (typeof target !== "string" || !live.has(target))
          throw new Error(
            "Import the referenced personal environments before importing this collection"
          )
        return target
      })
    ),
  ]
}
